import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { get, post, put } from '../../../shared/api/apiService';
import { useApiEssentials } from '../../../shared/hooks/useApiEssentials';
import { ActivityMessageListResponse } from '../ActivityPanel/types';
import { IntakeRequest, RequestStatus } from '../types';
import {
  IntakeAttachmentsResponseApi,
  IntakeRequestLogPagePayloadApi,
  IntakeRequestMessageApi,
  IntakeRequestsPageApi,
  IntakeRequestsPagePayloadApi,
  IntakeRequestStatusUpdateApi,
  IntakeSortApi,
  IntegrationRequestDetailsApi,
  SsoConnectorRequestDetailsApi,
} from '../IntakeRequest/apiTypes';
import {
  mapIntegrationRequestDetails,
  mapIntakeRequestListItem,
  mapIntakeRequestStatusToApi,
  mapIntakeAttachments,
  mapSsoConnectorRequestDetails,
  resolveIntakeRequestController,
} from '../IntakeRequest/mappers';

export interface UseIntakeRequestsOptions {
  pageSize?: number;
  page?: number;
  filters?: Array<{ field: string; value: string | string[] }>;
  search?: string;
  sort?: IntakeSortApi[];
}

export interface UseIntakeRequestDetailsOptions {
  id?: string;
  type?: string;
}

export interface UseUpdateIntakeRequestStatusOptions {
  id: string;
  status: RequestStatus;
  type: string;
  reason?: string;
}

export interface UseIntakeRequestLogsOptions {
  intakeId?: string;
  pageSize?: number;
  page?: number;
  types?: string[];
}

export interface UseIntakeRequestAttachmentsOptions {
  intakeId?: string;
}

export interface UseSendIntakeMessageOptions {
  id: string;
  email: string;
  message: string;
  ccRecipients: string[];
}

interface IntakeRequestsPage {
  elements: IntakeRequest[];
  total: number;
  totalPages: number;
  page: number;
  isFirst: boolean;
  isLast: boolean;
}

function mapIntakePage(api: IntakeRequestsPageApi): IntakeRequestsPage {
  return {
    ...api,
    elements: api.elements.map(mapIntakeRequestListItem),
  };
}

function mapSortFieldToBackend(
  field: string,
  direction: 'ASC' | 'DESC',
): IntakeSortApi[] {
  const fieldMapping: Record<string, string> = {
    id: 'requestId',
    submittedAt: 'createdDate',
    requestType: 'type',
    name: 'username',
    email: 'email',
    status: 'status',
    resolutionComment: 'resolutionComment',
  };

  return [{ field: fieldMapping[field] || field, direction }];
}

export function useIntakeRequests({
  pageSize = 10,
  page = 0,
  filters = [],
  search = '',
  sort = [],
}: UseIntakeRequestsOptions = {}) {
  const { getIdToken, apiUrl, isSignedIn } = useApiEssentials();

  return useQuery({
    queryKey: ['intake-requests', { pageSize, page, filters, search, sort }],
    queryFn: async () => {
      const idToken = await getIdToken();

      const payload: IntakeRequestsPagePayloadApi = {
        page,
        limit: pageSize,
        sort,
        filter: {},
      };

      const response = await post<IntakeRequestsPageApi>(
        '/requests/intake/page',
        payload,
        apiUrl,
        idToken,
      );

      return mapIntakePage(response);
    },
    enabled: isSignedIn,
    placeholderData: keepPreviousData,
    gcTime: 300000,
    retry: false,
    refetchOnWindowFocus: false,
    refetchInterval: 5 * 60 * 1000,
  });
}

// remove it once new counts user requests is implemented
export function useIntakeNewCount() {
  const { getIdToken, apiUrl, isSignedIn } = useApiEssentials();

  return useQuery({
    queryKey: ['intake-requests-new-count'],
    queryFn: async () => {
      const idToken = await getIdToken();

      const payload: IntakeRequestsPagePayloadApi = {
        page: 0,
        limit: 1,
        sort: mapSortFieldToBackend('status', 'ASC'),
        filter: { status: ['NEW'] },
      };

      const response = await post<IntakeRequestsPageApi>(
        '/requests/intake/page',
        payload,
        apiUrl,
        idToken,
      );

      return response.total;
    },
    enabled: isSignedIn,
    gcTime: 300000,
    retry: false,
    refetchOnWindowFocus: false,
    refetchInterval: 5 * 60 * 1000,
  });
}

export function useIntakeRequestDetails({
  id,
  type,
}: UseIntakeRequestDetailsOptions = {}) {
  const { getIdToken, apiUrl, isSignedIn } = useApiEssentials();

  return useQuery({
    queryKey: ['intake-request', { id, type }],
    queryFn: async () => {
      const idToken = await getIdToken();
      if (!id) throw new Error('No intake id provided');
      if (!type) throw new Error('No intake type provided');

      const controller = resolveIntakeRequestController(type);
      if (controller === 'integration') {
        const response = await get<IntegrationRequestDetailsApi>(
          `/requests/integration/${id}`,
          apiUrl,
          idToken,
        );
        return mapIntegrationRequestDetails(response);
      }

      const response = await get<SsoConnectorRequestDetailsApi>(
        `/requests/sso-connectors/${id}`,
        apiUrl,
        idToken,
      );
      return mapSsoConnectorRequestDetails(response);
    },
    enabled: isSignedIn && !!id && !!type,
    gcTime: 300000,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

export function useUpdateIntakeRequestStatus() {
  const { getIdToken, apiUrl } = useApiEssentials();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      status,
      reason,
      type,
    }: UseUpdateIntakeRequestStatusOptions) => {
      const idToken = await getIdToken();

      const controller = resolveIntakeRequestController(type);
      const payload: IntakeRequestStatusUpdateApi = {
        status: mapIntakeRequestStatusToApi(status),
        statusReason: reason,
      };

      const endpoint =
        controller === 'integration'
          ? `/requests/integration/${id}/status`
          : `/requests/sso-connectors/${id}/status`;

      return await put<void>(endpoint, payload, apiUrl, idToken);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['intake-requests'] });
      queryClient.invalidateQueries({
        queryKey: [
          'intake-request',
          { id: variables.id, type: variables.type },
        ],
      });
      queryClient.invalidateQueries({
        queryKey: ['intake-request-logs', { intakeId: variables.id }],
      });
    },
  });
}

export function useSendIntakeMessage() {
  const { getIdToken, apiUrl } = useApiEssentials();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      email,
      message,
      ccRecipients,
    }: UseSendIntakeMessageOptions) => {
      const idToken = await getIdToken();
      const payload: IntakeRequestMessageApi = { email, message, ccRecipients };

      return await post<void>(
        `/requests/intake/${id}/message`,
        payload,
        apiUrl,
        idToken,
      );
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['intake-request-logs', { intakeId: variables.id }],
      });
    },
  });
}

export function useIntakeRequestLogs({
  intakeId,
  pageSize = 50,
  page = 0,
  types = [],
}: UseIntakeRequestLogsOptions = {}) {
  const { getIdToken, apiUrl, isSignedIn } = useApiEssentials();

  return useQuery({
    queryKey: ['intake-request-logs', { intakeId, pageSize, page, types }],
    queryFn: async () => {
      const idToken = await getIdToken();
      if (!intakeId) throw new Error('No intake request id provided');

      const payload: IntakeRequestLogPagePayloadApi = {
        page,
        limit: pageSize,
        sort: [{ field: 'createdDate', direction: 'DESC' }],
        filter: {
          intakeId,
          ...(types.length > 0 ? { types } : {}),
        },
      };

      const response = await post<ActivityMessageListResponse>(
        '/requests/intake/log/page',
        payload,
        apiUrl,
        idToken,
      );

      return {
        ...response,
        elements: response.elements.map(element => ({
          ...element,
          supportRequestId:
            typeof element.supportRequestId === 'string'
              ? element.supportRequestId
              : intakeId,
        })),
      };
    },
    enabled: isSignedIn && !!intakeId,
    placeholderData: keepPreviousData,
    gcTime: 300000,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

export function useIntakeRequestAttachments({
  intakeId,
}: UseIntakeRequestAttachmentsOptions = {}) {
  const { getIdToken, apiUrl, isSignedIn } = useApiEssentials();

  return useQuery({
    queryKey: ['intake-request-attachments', { intakeId }],
    queryFn: async () => {
      const idToken = await getIdToken();
      if (!intakeId) throw new Error('No intake request id provided');

      const response = await get<IntakeAttachmentsResponseApi>(
        `/requests/intake/${intakeId}/attachments`,
        apiUrl,
        idToken,
      );

      return mapIntakeAttachments(response.elements);
    },
    enabled: isSignedIn && !!intakeId,
    placeholderData: keepPreviousData,
    gcTime: 300000,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

export { mapSortFieldToBackend as mapIntakeSortFieldToBackend };
